'use client'

import { Icon } from '@/components/ui/icon'
import { useCurrency } from '@/hooks/use-currency'
import { useTapLock } from '@/hooks/use-tap-lock'
import { useUnitOfMeasure } from '@/hooks/useUnitOfMeasure'
import { useAppDispatch } from '@/lib/store'
import { addToCart, openCartDrawer } from '@/lib/store/features/cart/cartSlice'
import { getUnitDisplay, resolveUnitOfMeasure } from '@/lib/types/core/unit-of-measure'
import { ProductItem } from '@/lib/types/operations/product'
import { IProductVariantExtended } from '@/lib/types/operations/product-variant'
import {
  cartVariantAttributesSchema,
  selectedVariantStateSchema
} from '@/lib/validation/input/product-variant-selection-schema'
import { Button } from '@/components/ui/button'
import { showToast, ToastSuppress } from '@/lib/utils/toast'
import type { PriceTier } from '@/lib/utils/price-resolution'
import { useRouter } from 'next/navigation'
import { useCallback, useMemo, useState } from 'react'
import { VariantSelector, calculateVariantPrice, useVariantSelection } from './variant-selector'
import type { UseVariantSelectionResult } from './variant-selector'

interface ProductInventoryStatus {
  hasVariants: boolean
  inStock: boolean
  availableQuantity: number
  variantInventory: Array<{
    variantId: string
    inventory: number
    inStock: boolean
  }>
}

interface UnifiedProductActionsProps {
  product: ProductItem
  productStats: ProductInventoryStatus
  // Normally owned by the parent (ProductContent) and shared with
  // ProductMediaGallery, so the main preview image can follow the selected
  // colour. Optional so a caller that only needs the actions (e.g. a test
  // rendering this component in isolation) can still omit it; this component
  // then falls back to its own instance.
  variantSelection?: UseVariantSelectionResult
  className?: string
  priceTier?: PriceTier
}

export function UnifiedProductActions({ product, productStats, variantSelection: variantSelectionProp, className, priceTier = 'RETAIL' }: UnifiedProductActionsProps) {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const { format: formatCurrency } = useCurrency()
  const [simpleQuantity, setSimpleQuantity] = useState(1)
  // Synchronous lock shared by Add to Cart and Checkout: a rapid double-tap
  // (or Add then Checkout inside the window) must not double the quantity
  const acquireTapLock = useTapLock()

  const hasVariants = useMemo(() => {
    return Array.isArray(product.variants) && product.variants.length > 0
  }, [product.variants])

  // Hooks can't be conditional, so this always runs; its result is only used
  // when no caller-owned variantSelection was passed in.
  const ownVariantSelection = useVariantSelection(
    (product.variants || []) as IProductVariantExtended[],
    productStats,
    priceTier
  )
  const variantSelection = variantSelectionProp ?? ownVariantSelection

  // Unit of measure for simple products
  const unitMeasure = useUnitOfMeasure(product.unitOfMeasure, null, product)

  const handleAddToCart = useCallback(() => {
    try {
      if (hasVariants && variantSelection.selectedVariant) {
        const sv = variantSelection.selectedVariant
        const sel = variantSelection.selection

        // Validate
        const validationResult = selectedVariantStateSchema.safeParse({
          variantId: sel.variantId,
          quantity: sel.quantity,
          isFullRoll: sel.mode === 'roll',
          error: null,
          selectedAttributes: {}
        })
        if (!validationResult.success) {
          showToast({
            title: 'Please check your selection',
            description: 'Make sure you\'ve chosen a color and quantity.',
            color: 'warning',
            timeout: 5000
          })
          return
        }

        const isFullRoll = sel.mode === 'roll'
        const variantPrice = calculateVariantPrice(sv, isFullRoll ? 'roll' : 'cut', sel.quantity, priceTier)

        const variantDisplayName = `${product.name} - ${sv.name}`
        const variantUnitOfMeasure = resolveUnitOfMeasure(sv)

        const attrValues = (sv.attributeValues as Record<string, any> | undefined) || {}
        const attributes = {
          variantId: sv.id,
          color: String(attrValues.COLOR || sv.color || ''),
          dimensions: `${String(sv.widthInches || '')}×${String(sv.heightInches || '')}`,
          unitOfMeasure: variantUnitOfMeasure,
          isFullRoll,
          lengthInUnits: sel.quantity,
          totalUnits: sel.quantity
        }

        const attributesValidation = cartVariantAttributesSchema.safeParse(attributes)
        if (!attributesValidation.success) {
          showToast({
            title: 'Something\'s off with that variant',
            description: 'Please reload the page and try again.',
            color: 'danger',
            timeout: 5000
          })
          return
        }

        // Acquired after validation so a corrected retry is never blocked;
        // a second tap inside the lock window is a no-op
        if (!acquireTapLock()) return

        dispatch(
          addToCart({
            productId: product.id || '',
            quantity: sel.quantity,
            variantId: sv.id,
            product: { ...product, name: variantDisplayName, price: variantPrice },
            purchaseMode: isFullRoll ? 'full-roll' : 'custom-cut',
            pieces: 1,
            lengthPerPiece: isFullRoll ? undefined : sel.quantity,
            unitOfMeasure: variantUnitOfMeasure
          })
        )

        const unitDisplay = getUnitDisplay(variantUnitOfMeasure)
        showToast({
          title: `Added ${sel.quantity} ${unitDisplay} to cart`,
          color: 'success',
          timeout: 3000,
          // Cart drawer opens right after this — on mobile the drawer
          // covers the whole screen so the toast is redundant.
          skipIf: ToastSuppress.onMobile
        })
      } else {
        // Simple product
        // Second tap inside the lock window is a no-op, not a quantity increment
        if (!acquireTapLock()) return
        const productUnitOfMeasure = resolveUnitOfMeasure(product)
        dispatch(
          addToCart({
            productId: product.id || '',
            quantity: simpleQuantity,
            product,
            unitOfMeasure: productUnitOfMeasure
          })
        )
        showToast({
          title: `Added ${unitMeasure.formatQuantity(simpleQuantity)} to cart`,
          color: 'success',
          timeout: 3000,
          skipIf: ToastSuppress.onMobile
        })
      }

      dispatch(openCartDrawer())
    } catch (error) {
      console.error('Failed to add to cart:', error)
      showToast({
        title: 'Could not add to cart',
        description: 'Please try again in a moment.',
        color: 'danger',
        timeout: 5000
      })
    }
  }, [product, hasVariants, variantSelection, simpleQuantity, dispatch, unitMeasure, acquireTapLock])

  const handleCheckout = useCallback(() => {
    handleAddToCart()
    router.push('/checkout')
  }, [handleAddToCart, router])

  const isButtonDisabled = hasVariants
    ? !variantSelection.isValid
    : !productStats.inStock

  const buttonText = hasVariants
    ? (variantSelection.validationError || `Add to Cart: ${formatCurrency(variantSelection.derivedPrice)}`)
    : 'Add to Cart'

  return (
    <div className={className}>
      {/* Variant Selector — variable products (visible on all screen sizes) */}
      {hasVariants && (
        <div className='mb-4'>
          <VariantSelector
            variants={(product.variants || []) as IProductVariantExtended[]}
            variantInventory={productStats.variantInventory}
            selection={variantSelection.selection}
            selectedVariant={variantSelection.selectedVariant}
            canShowRollMode={variantSelection.canShowRollMode}
            derivedPrice={variantSelection.derivedPrice}
            derivedUnitPrice={variantSelection.derivedUnitPrice}
            availableInventory={variantSelection.availableInventory}
            minQty={variantSelection.minQty}
            maxQty={variantSelection.maxQty}
            stepSize={variantSelection.stepSize}
            onSelectColor={variantSelection.setColor}
            onSelectMode={variantSelection.setMode}
            onSelectQuantity={variantSelection.setQuantity}
          />
        </div>
      )}

      {/* Quantity Stepper — simple products only (desktop) */}
      {!hasVariants && productStats.inStock && (
        <div className='hidden md:flex items-center gap-3 mb-4'>
          <span className='text-sm text-muted'>Qty:</span>
          <div className='flex items-center gap-1'>
            <Button
              isIconOnly
              size='sm'
              variant='outline'
              aria-label='Decrease quantity'
              onPress={() => setSimpleQuantity(prev => Math.max(1, prev - 1))}
              isDisabled={simpleQuantity <= 1}
            >
              <Icon icon='minus' className='w-4 h-4' />
            </Button>
            <span className='w-10 text-center text-sm font-medium'>{simpleQuantity}</span>
            <Button
              isIconOnly
              size='sm'
              variant='outline'
              aria-label='Increase quantity'
              onPress={() => setSimpleQuantity(prev => prev + 1)}
              isDisabled={simpleQuantity >= (productStats.availableQuantity || 99)}
            >
              <Icon icon='plus' className='w-4 h-4' />
            </Button>
          </div>
        </div>
      )}

      {/* Add to Cart + Checkout Buttons */}
      <div className='space-y-2'>
        <Button
          variant='primary'
          size='lg'
          className='w-full'
          onPress={handleAddToCart}
          isDisabled={isButtonDisabled}
          data-testid='add-to-cart-cta'
        >
          <Icon icon='cart-plus' className='text-xl' aria-hidden='true' /> {buttonText}
        </Button>
        <Button
          size='lg'
          variant='outline'
          className='w-full'
          isDisabled={isButtonDisabled}
          onPress={handleCheckout}
        >
          <Icon icon='schedule' className='text-lg' aria-hidden='true' /> Checkout
        </Button>
      </div>
    </div>
  )
}
